import React, { useEffect, useRef, useState } from "react";
import { Button } from "reactstrap";

import { StateCallbacks } from "../state";
import { extractKeysAndIndexes } from "../state/getDefaultState";
import { State, TaxonomyEntry } from "../types";

import "./styles.scss";

type Props = Readonly<{
  review: boolean;
  state: State;
  apiKey: string;
  stateCallbacks: StateCallbacks;
}>;

export enum TextMode {
  WHOLE = "whole",
  BLOCK = "block",
  CUSTOM = "custom"
}

export const OpenAi = ({ review, state, stateCallbacks, apiKey }: Props) => {
  
  const [generatedText, setGeneratedText] = useState<string>("");
  const [showOpenai, setShowOpenai] = useState<boolean>(false);
  const [textMode, setTextMode] = useState<TextMode>(TextMode.BLOCK);
  const [textToAnalyze, setTextToAnalyze] = useState<string>("");

  useEffect(() => {
    let text = "";
    if (!state?.activeValue) {
      return;
    }
    switch (textMode) {
      case TextMode.BLOCK :
        if (state.values[state.activeValue].idRef) {
          text = state.blocks[state.values[state.activeValue].idRef].text
        }
    }
    setTextToAnalyze(text) // not correct

  }, [textMode, state.activeValue, state.values]);

  const fetchSummary = async (taxoEntry: TaxonomyEntry) => {
    let finalPrompt = "The passage between characters Φ and Ω has information of type " + taxoEntry.type + ": \n";
    finalPrompt += "\nΦ\n" + textToAnalyze + "\nΩ\n"
    finalPrompt += taxoEntry.prompt + "\n";
    finalPrompt += "If unclear or undefined, answer with N/A:";
    console.log(finalPrompt);
    const resp = await fetch('https://api.openai.com/v1/completions',
    {
      
      method: 'post',
      body: JSON.stringify({
        model: 'text-davinci-003',
        temperature: 0.5,
        max_tokens: 500,
        stream: true,
        prompt: finalPrompt
      }),
      headers: {
        'authorization': 'Bearer ' + apiKey,
        'accept': 'text/event-stream',
        'content-type':'application/json'
      },
    });
    const reader = resp.body?.getReader();
    let generatedText = "";
    while(true) {
      const chunk = await reader?.read();
      if (chunk?.done) {
        break;
      }
      let chunkString = new TextDecoder("utf-8").decode(chunk.value);
      let chunkLines = chunkString.split("\n\n");
      chunkLines.forEach(chunkLine => {
        let chunkJson = chunkLine.substring("data: ".length);
        try {
          if (chunkJson && chunkJson != '[DONE]') {
            let chunkContent = JSON.parse(chunkJson);

            generatedText += chunkContent.choices[0].text;
            generatedText = generatedText.trimLeft();
            setGeneratedText(generatedText);
          }
        } catch (e) {
          console.log(e);
          console.log(chunkJson);
        }
      })
    }
  }

  let generate = (taxoEntry : TaxonomyEntry) => {
    setShowOpenai(true);
    fetchSummary(taxoEntry);
  }

  if (!state.activeValue) {
    return (
      <div className="no-point">
        <i className="fa fa-warning"></i>
        Please select a data point
      </div>
    );
  }
  let value = state.values[state.activeValue];
  let taxoEntry = extractKeysAndIndexes(state.activeValue, state.taxonomy.taxonomyObject).taxonomyEntry;

  return (
    <div className="flex-container">
      <div className="point-label">{taxoEntry?.question}</div>
      <div>
        <div className="prediction-label">Prediction</div>
        <div className="prediction-value">{value?.valueRaw}</div>
      </div>
      <div>
        <div>
          <div className="prompt-label">
            Prompt
            <span className="openai-generateButton">
            <Button
              color="primary"
              disabled={!textToAnalyze || !taxoEntry.prompt}
              onClick={() => {generate(taxoEntry)}}
              type="button"
            >
              Generate
            </Button>
            </span>
          </div>
          <textarea 
            className="prompt-value" 
            value={taxoEntry.prompt} 
            onChange={e => {
              stateCallbacks.updatePointPrompt(state.activeValue, e.target.value)
            }}></textarea>
        </div>
      </div>
      <div className="radio-container">
        <div>
          <input 
            onChange={((e) => setTextMode(e.target.value))}
            type="radio" 
            value={TextMode.BLOCK}
            checked={textMode === TextMode.BLOCK}
            name="viewmode"
          />
          <span>Use associated block text</span>
        </div>
        <div>
          <input 
            disabled={true}
            onChange={((e) => setTextMode(e.target.value))}
            type="radio" 
            value={TextMode.WHOLE}
            checked={textMode === TextMode.WHOLE}
            name="viewmode"/>
          <span>Use entire text</span>
        </div>
        <div>
          <input 
            disabled={true}
            onChange={((e) => setTextMode(e.target.value))}
            type="radio" 
            value={TextMode.CUSTOM}
            checked={textMode === TextMode.CUSTOM}
            name="viewmode"
          />
          <span>Use custom text</span>
        </div>
      </div>
      {showOpenai && 
      <div>
        <div className="openai-label">Openai output
        <span className="openai-generateButton">
            <Button
              disabled={!generatedText || generatedText === value.value}
              color="primary"
              onClick={() => {
                stateCallbacks.onUpdateValue(state.activeValue, generatedText, generatedText);
              }}
              type="button"
            >
              Replace prediction
            </Button>
            </span>
        </div>
        <textarea className="openai-value" contentEditable={false} value={generatedText}></textarea>
      </div>
      }
    </div>
  );
};
OpenAi.displayName = "Questions";
